import React, { useState, useEffect, useMemo } from "react";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";

function calcSchedule(balance, annualRate, termMonths, monthlyPayment, extraMonthly = 0, lumpSum = 0, lumpSumMonth = 0) {
  const monthlyRate = annualRate / 100 / 12;
  const rows = [];
  let bal = balance;
  let totalInterest = 0;

  if (lumpSum > 0 && lumpSumMonth === 0) bal = Math.max(0, bal - lumpSum);

  for (let m = 1; m <= termMonths && bal > 0; m++) {
    if (lumpSum > 0 && lumpSumMonth === m) bal = Math.max(0, bal - lumpSum);

    const interest = bal * monthlyRate;
    const scheduled = Math.min(monthlyPayment, bal + interest);
    const extra = Math.min(extraMonthly, Math.max(0, bal + interest - scheduled));
    const principal = scheduled - interest + extra;
    totalInterest += interest;
    bal = Math.max(0, bal - principal);

    rows.push({ month: m, payment: scheduled + extra, principal, interest, balance: bal, totalInterest });
    if (bal <= 0.01) break;
  }
  return rows;
}

function StatBox({ label, value, accent }) {
  return (
    <div className="card" style={{ padding: "18px 22px", borderColor: accent ? `${accent}33` : "var(--border)" }}>
      {accent && <div style={{ height: "2px", background: accent, marginBottom: "12px", borderRadius: "1px" }} />}
      <div style={{ fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: "6px" }}>{label}</div>
      <div style={{ fontSize: "22px", fontFamily: "var(--font-display)", fontWeight: 800, color: accent || "var(--text-primary)" }}>{value}</div>
    </div>
  );
}

export default function Amortization() {
  const { isAdmin } = useAuth();
  const [loan, setLoan] = useState({ propertyName: "", originalBalance: "", currentBalance: "", interestRate: "", termMonths: "", monthlyPayment: "", startDate: "" });
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [extraMonthly, setExtraMonthly] = useState(0);
  const [lumpSum, setLumpSum] = useState(0);
  const [lumpSumMonth, setLumpSumMonth] = useState(1);
  const [showFullTable, setShowFullTable] = useState(false);
  const [activeTab, setActiveTab] = useState("schedule"); // 'schedule' | 'calculator'

  useEffect(() => {
    getDoc(doc(db, "loan", "current")).then(d => {
      if (d.exists()) setLoan(d.data());
      setLoading(false);
    });
  }, []);

  async function saveLoan() {
    setSaving(true);
    try {
      await setDoc(doc(db, "loan", "current"), { ...loan, updatedAt: serverTimestamp() });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    setSaving(false);
  }

  const bal = parseFloat(loan.currentBalance) || 0;
  const rate = parseFloat(loan.interestRate) || 0;
  const term = parseInt(loan.termMonths) || 0;
  const pmt = parseFloat(loan.monthlyPayment) || 0;

  const baseSchedule = useMemo(() => bal && rate && term && pmt ? calcSchedule(bal, rate, term, pmt) : [], [bal, rate, term, pmt]);
  const modSchedule = useMemo(() => bal && rate && term && pmt ? calcSchedule(bal, rate, term, pmt, extraMonthly, lumpSum, lumpSumMonth) : [], [bal, rate, term, pmt, extraMonthly, lumpSum, lumpSumMonth]);

  const baseTotalInterest = baseSchedule.at(-1)?.totalInterest || 0;
  const modTotalInterest = modSchedule.at(-1)?.totalInterest || 0;
  const interestSaved = baseTotalInterest - modTotalInterest;
  const monthsSaved = baseSchedule.length - modSchedule.length;

  const displayRows = showFullTable ? baseSchedule : baseSchedule.slice(0, 24);

  if (loading) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "400px" }}>
      <div style={{ width: "36px", height: "36px", border: "3px solid var(--border)", borderTop: "3px solid var(--accent)", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
    </div>
  );

  return (
    <div style={styles.page} className="animate-in">
      <div style={styles.header}>
        <h1 style={styles.title}>Loan Schedule</h1>
        <p style={styles.subtitle}>Amortization schedule and extra payment modeling</p>
      </div>

      {/* Loan details — admin editable */}
      <div className="card" style={{ padding: "24px", marginBottom: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div>
            <h2 style={styles.cardTitle}>Loan Details</h2>
            {!isAdmin && <p style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "2px" }}>Contact an admin to update loan details</p>}
          </div>
          {isAdmin && (
            <button className="btn-primary" onClick={saveLoan} disabled={saving}>
              {saving ? "Saving..." : saved ? "✓ Saved" : "Save Changes"}
            </button>
          )}
        </div>

        <div style={styles.loanGrid}>
          {[
            { key: "propertyName", label: "Property Name", type: "text", placeholder: "e.g. 123 Main St" },
            { key: "originalBalance", label: "Original Loan Amount ($)", type: "number", placeholder: "500000" },
            { key: "currentBalance", label: "Current Balance ($)", type: "number", placeholder: "480000" },
            { key: "interestRate", label: "Interest Rate (%)", type: "number", placeholder: "6.5", step: "0.01" },
            { key: "termMonths", label: "Loan Term (months)", type: "number", placeholder: "360" },
            { key: "monthlyPayment", label: "Monthly Payment ($)", type: "number", placeholder: "3160" },
            { key: "startDate", label: "Loan Start Date", type: "date" },
          ].map(field => (
            <div key={field.key}>
              <label style={styles.label}>{field.label}</label>
              <input
                type={field.type}
                step={field.step}
                value={loan[field.key] || ""}
                onChange={e => setLoan(l => ({ ...l, [field.key]: e.target.value }))}
                placeholder={field.placeholder}
                className="input-field"
                disabled={!isAdmin}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      {bal > 0 && pmt > 0 && (
        <>
          <div style={styles.statsGrid}>
            <StatBox label="Current Balance" value={`$${bal.toLocaleString()}`} accent="#6c63ff" />
            <StatBox label="Monthly Payment" value={`$${pmt.toLocaleString()}`} accent="#00d68f" />
            <StatBox label="Total Interest (original)" value={`$${baseTotalInterest.toLocaleString("en-US", { maximumFractionDigits: 0 })}`} accent="#ff5c6c" />
            <StatBox label="Payoff In" value={`${baseSchedule.length} months`} accent="#ffd166" />
          </div>

          {/* Tabs */}
          <div style={styles.tabs}>
            {["schedule", "calculator"].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{ ...styles.tab, ...(activeTab === tab ? styles.tabActive : {}) }}
              >
                {tab === "schedule" ? "📋 Amortization Schedule" : "🧮 Extra Payment Calculator"}
              </button>
            ))}
          </div>

          {/* Amortization table */}
          {activeTab === "schedule" && (
            <div className="card" style={{ padding: "24px" }}>
              <h2 style={{ ...styles.cardTitle, marginBottom: "16px" }}>Month-by-Month Schedule</h2>
              <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {["Month", "Payment", "Principal", "Interest", "Remaining Balance"].map(h => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((row, i) => (
                      <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                        <td style={styles.td}>{row.month}</td>
                        <td style={styles.td}>${row.payment.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ ...styles.td, color: "var(--green)" }}>${row.principal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ ...styles.td, color: "var(--red)" }}>${row.interest.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td style={{ ...styles.td, fontWeight: 600 }}>${row.balance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {baseSchedule.length > 24 && (
                <button
                  onClick={() => setShowFullTable(!showFullTable)}
                  className="btn-secondary"
                  style={{ marginTop: "16px", width: "100%" }}
                >
                  {showFullTable ? "Show less" : `Show all ${baseSchedule.length} months`}
                </button>
              )}
            </div>
          )}

          {/* Calculator */}
          {activeTab === "calculator" && (
            <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: "20px", alignItems: "start" }}>
              <div className="card" style={{ padding: "24px" }}>
                <h2 style={{ ...styles.cardTitle, marginBottom: "20px" }}>Model Extra Payments</h2>

                <div style={{ marginBottom: "20px" }}>
                  <label style={styles.label}>Extra Monthly Principal ($)</label>
                  <input
                    type="number"
                    value={extraMonthly}
                    onChange={e => setExtraMonthly(parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    className="input-field"
                    min="0"
                  />
                  <div style={styles.inputHint}>Added to every monthly payment</div>
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <label style={styles.label}>One-Time Lump Sum ($)</label>
                  <input
                    type="number"
                    value={lumpSum}
                    onChange={e => setLumpSum(parseFloat(e.target.value) || 0)}
                    placeholder="0"
                    className="input-field"
                    min="0"
                  />
                </div>

                {lumpSum > 0 && (
                  <div style={{ marginBottom: "20px" }}>
                    <label style={styles.label}>Apply Lump Sum at Month #</label>
                    <input
                      type="number"
                      value={lumpSumMonth}
                      onChange={e => setLumpSumMonth(parseInt(e.target.value) || 1)}
                      min="0"
                      max={baseSchedule.length}
                      className="input-field"
                    />
                    <div style={styles.inputHint}>0 = apply immediately</div>
                  </div>
                )}

                <button className="btn-secondary" onClick={() => { setExtraMonthly(0); setLumpSum(0); setLumpSumMonth(1); }} style={{ width: "100%" }}>
                  Reset
                </button>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div style={styles.savingsBox}>
                  <div style={styles.savingsTitle}>📊 Impact Summary</div>
                  <div style={styles.savingsGrid}>
                    <div style={styles.savingsStat}>
                      <div style={styles.savingsLabel}>Original Payoff</div>
                      <div style={styles.savingsValue}>{baseSchedule.length} months</div>
                    </div>
                    <div style={styles.savingsStat}>
                      <div style={styles.savingsLabel}>New Payoff</div>
                      <div style={{ ...styles.savingsValue, color: "var(--green)" }}>{modSchedule.length} months</div>
                    </div>
                    <div style={styles.savingsStat}>
                      <div style={styles.savingsLabel}>Time Saved</div>
                      <div style={{ ...styles.savingsValue, color: "var(--green)" }}>
                        {monthsSaved > 0 ? `${monthsSaved} months (${(monthsSaved / 12).toFixed(1)} yrs)` : "—"}
                      </div>
                    </div>
                    <div style={styles.savingsStat}>
                      <div style={styles.savingsLabel}>Interest Saved</div>
                      <div style={{ ...styles.savingsValue, color: "var(--green)" }}>
                        {interestSaved > 0 ? `$${interestSaved.toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "—"}
                      </div>
                    </div>
                    <div style={styles.savingsStat}>
                      <div style={styles.savingsLabel}>Original Total Interest</div>
                      <div style={{ ...styles.savingsValue, color: "var(--red)" }}>${baseTotalInterest.toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
                    </div>
                    <div style={styles.savingsStat}>
                      <div style={styles.savingsLabel}>New Total Interest</div>
                      <div style={{ ...styles.savingsValue, color: "var(--red)" }}>${modTotalInterest.toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
                    </div>
                  </div>
                </div>

                {/* Modified schedule preview */}
                {(extraMonthly > 0 || lumpSum > 0) && (
                  <div className="card" style={{ padding: "20px" }}>
                    <h3 style={{ ...styles.cardTitle, marginBottom: "12px", fontSize: "14px" }}>Modified Schedule (first 12 months)</h3>
                    <div style={{ overflowX: "auto" }}>
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            {["Month", "Payment", "Principal", "Interest", "Balance"].map(h => (
                              <th key={h} style={styles.th}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {modSchedule.slice(0, 12).map((row, i) => (
                            <tr key={i}>
                              <td style={styles.td}>{row.month}</td>
                              <td style={styles.td}>${row.payment.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td style={{ ...styles.td, color: "var(--green)" }}>${row.principal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td style={{ ...styles.td, color: "var(--red)" }}>${row.interest.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                              <td style={{ ...styles.td, fontWeight: 600 }}>${row.balance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {!bal && (
        <div className="card" style={{ padding: "48px", textAlign: "center" }}>
          <div style={{ fontSize: "36px", marginBottom: "12px" }}>🏠</div>
          <div style={{ color: "var(--text-muted)", fontSize: "14px" }}>
            {isAdmin ? "Enter loan details above to generate the amortization schedule." : "Loan details have not been configured yet. Contact an admin."}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { maxWidth: "1100px", margin: "0 auto" },
  header: { marginBottom: "24px" },
  title: { fontSize: "28px", fontFamily: "var(--font-display)", fontWeight: 800 },
  subtitle: { color: "var(--text-muted)", fontSize: "14px", marginTop: "4px" },
  cardTitle: { fontSize: "16px", fontFamily: "var(--font-display)", fontWeight: 700 },
  loanGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" },
  label: { display: "block", fontSize: "11px", color: "var(--text-muted)", marginBottom: "6px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "16px", marginBottom: "24px" },
  tabs: { display: "flex", gap: "8px", marginBottom: "20px" },
  tab: {
    padding: "10px 20px", borderRadius: "var(--radius-sm)", background: "var(--bg-card)",
    border: "1px solid var(--border)", color: "var(--text-secondary)", fontSize: "14px",
    fontWeight: 500, cursor: "pointer", transition: "var(--transition)",
  },
  tabActive: { background: "var(--accent-dim)", border: "1px solid var(--border-accent)", color: "var(--accent-bright)" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left", padding: "10px 12px", fontSize: "11px",
    color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.08em",
    borderBottom: "1px solid var(--border)",
  },
  td: { padding: "11px 12px", fontSize: "13px", color: "var(--text-primary)", borderBottom: "1px solid rgba(255,255,255,0.03)" },
  inputHint: { fontSize: "11px", color: "var(--text-muted)", marginTop: "5px" },
  savingsBox: {
    background: "var(--bg-card)", border: "1px solid var(--border)",
    borderRadius: "var(--radius-lg)", padding: "24px",
  },
  savingsTitle: { fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "16px", marginBottom: "20px" },
  savingsGrid: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "16px" },
  savingsStat: { padding: "12px", background: "var(--bg-secondary)", borderRadius: "var(--radius-sm)" },
  savingsLabel: { fontSize: "11px", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "6px" },
  savingsValue: { fontSize: "18px", fontFamily: "var(--font-display)", fontWeight: 800 },
};
